import {
  forwardRef,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { cn } from "@/lib/utils";

interface ScaledSlideIframeProps {
  src: string;
  aspectRatio: "16:9" | "4:3";
  title: string;
  className?: string;
  allow?: string;
  sandbox?: string;
}

// Renders a slide iframe at a fixed *logical* 1920×1080 (16:9) or 1440×1080
// (4:3) viewport, then uses CSS `transform: scale(N)` to shrink it to fit
// the wrapper. The iframe's `contentWindow.innerWidth/innerHeight` always
// equal the logical size, so slide CSS that uses viewport units (or any
// other absolute sizing) sees the deck's design dimensions regardless of
// how big the host pane is. Without this, decks authored for ~1920×1080
// get clipped at the bottom when rendered into the presenter mirror /
// in-page Present overlay (typically smaller boxes), and `overflow:
// hidden` on the host swallows the overflow.
//
// ResizeObserver tracks the wrapper, recomputes scale, recenters the
// iframe via negative margins. Same trick `ResponsiveIframeViewer` uses
// for the editor preview path.
export const ScaledSlideIframe = forwardRef<
  HTMLIFrameElement,
  ScaledSlideIframeProps
>(function ScaledSlideIframe(
  { src, aspectRatio, title, className, allow, sandbox },
  forwardedRef,
) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLIFrameElement | null>(null);
  const [scale, setScale] = useState(1);

  const logical = useMemo(
    () => ({
      width: aspectRatio === "4:3" ? 1440 : 1920,
      height: 1080,
    }),
    [aspectRatio],
  );

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const compute = () => {
      const rect = wrapper.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const s = Math.min(
        rect.width / logical.width,
        rect.height / logical.height,
      );
      setScale(s);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(wrapper);
    return () => ro.disconnect();
  }, [logical.width, logical.height]);

  const scaledWidth = logical.width * scale;
  const scaledHeight = logical.height * scale;

  return (
    <div
      ref={wrapperRef}
      className={cn(
        "relative w-full h-full overflow-hidden bg-black",
        className,
      )}
    >
      <iframe
        ref={(el) => {
          innerRef.current = el;
          if (typeof forwardedRef === "function") forwardedRef(el);
          else if (forwardedRef) forwardedRef.current = el;
        }}
        src={src}
        title={title}
        allow={allow}
        sandbox={sandbox}
        style={{
          position: "absolute",
          width: `${logical.width}px`,
          height: `${logical.height}px`,
          left: "50%",
          top: "50%",
          marginLeft: `${-scaledWidth / 2}px`,
          marginTop: `${-scaledHeight / 2}px`,
          transformOrigin: "top left",
          transform: `scale(${scale})`,
          border: 0,
          backgroundColor: "white",
        }}
      />
    </div>
  );
});
